import { useCallback, useEffect, useMemo, useRef } from 'react';
import useSWRInfinite from 'swr/infinite';
import { EngineService } from '@/services/engine';
import { Event } from '@/types/event';
import { PaginatedResponse } from '@/types/responses';
import { getSourceEventsCacheKey } from '@/constants/cache-keys';
import { usePagesStateStore } from '@/stores';
import { EVENTS_PAGE_SIZE } from '@/features/EventsListLayout/hooks/eventsDataShared';

interface UseSourceEventsParams {
    page?: number;
    size?: number;
    orderBy?: string;
    classId?: readonly number[];
}

const DEFAULT_PARAMS: UseSourceEventsParams = {
    page: 1,
    size: EVENTS_PAGE_SIZE,
    orderBy: 'createdAt desc',
};

const SWR_OPTIONS = {
    revalidateOnFocus: false,
} as const;

export function useSourceEvents(
    sourceId: string,
    params?: UseSourceEventsParams,
    refreshInterval?: number,
    pageKey?: string,
) {
    const mergedParams = { ...DEFAULT_PARAMS, ...params };
    const { getPageState, setPageState } = usePagesStateStore();
    const classIdKey = mergedParams.classId?.join(',') ?? '';

    const cacheKey = useMemo(() => {
        if (!sourceId) return null;
        return getSourceEventsCacheKey(
            sourceId,
            mergedParams.orderBy,
            mergedParams.classId ? [...mergedParams.classId] : undefined,
        );
    }, [sourceId, mergedParams.orderBy, classIdKey]);

    const getKey = useCallback(
        (
            pageIndex: number,
            previousPageData: PaginatedResponse<Event> | null,
        ) => {
            if (!sourceId || !cacheKey) return null;
            if (previousPageData && !previousPageData.items.length) return null;

            return [cacheKey, pageIndex];
        },
        [sourceId, cacheKey],
    );

    const { data, error, size, setSize, isLoading, isValidating, mutate } =
        useSWRInfinite<PaginatedResponse<Event>>(
            getKey,
            ([, pageIndex]) =>
                EngineService.searchSourceEvents(sourceId, {
                    page: (pageIndex as number) + 1,
                    size: mergedParams.size,
                    orderBy: mergedParams.orderBy,
                    classId: mergedParams.classId
                        ? [...mergedParams.classId]
                        : undefined,
                }),
            { ...SWR_OPTIONS, refreshInterval },
        );

    const previousCacheKey = useRef<string | null>(null);
    const restoredKeyRef = useRef<string | null>(null);
    const restoringSizeRef = useRef<{ key: string; size: number } | null>(null);

    useEffect(() => {
        if (!cacheKey) return;

        if (previousCacheKey.current === null) {
            previousCacheKey.current = cacheKey;
            return;
        }

        if (previousCacheKey.current !== cacheKey) {
            previousCacheKey.current = cacheKey;
            restoredKeyRef.current = null;
            restoringSizeRef.current = null;
            setSize(1);
        }
    }, [cacheKey, setSize]);

    useEffect(() => {
        if (!sourceId || !pageKey) return;
        if (restoredKeyRef.current === pageKey) return;

        const { size: storedSize } = getPageState(pageKey);
        restoredKeyRef.current = pageKey;
        restoringSizeRef.current = { key: pageKey, size: storedSize };
        setSize(storedSize);
    }, [sourceId, pageKey, getPageState, setSize]);

    useEffect(() => {
        if (pageKey && sourceId && size >= 1) {
            if (
                restoringSizeRef.current?.key === pageKey &&
                restoringSizeRef.current.size !== size
            ) {
                return;
            }

            const current = getPageState(pageKey);
            setPageState(pageKey, { ...current, size });
            if (
                restoringSizeRef.current?.key === pageKey &&
                restoringSizeRef.current.size === size
            ) {
                restoringSizeRef.current = null;
            }
        }
    }, [pageKey, sourceId, size, getPageState, setPageState]);

    const events = useMemo(() => {
        if (!data?.length) return [];

        const seen = new Set<string>();
        const result: Event[] = [];
        for (const event of data.flatMap((page) => page.items)) {
            if (seen.has(event.id)) continue;
            seen.add(event.id);
            result.push(event);
        }
        return result;
    }, [data]);

    const hasMore = useMemo(() => {
        if (!data || data.length === 0) return false;
        const lastPage = data[data.length - 1];
        return lastPage.page.page < lastPage.page.totalPages;
    }, [data]);

    const isLoadingMore = isValidating && size > 1;

    const loadMore = useCallback(() => {
        if (!isValidating && hasMore) {
            void setSize(size + 1);
        }
    }, [hasMore, isValidating, setSize, size]);

    return {
        events,
        error,
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        mutate,
    };
}
